/**
 * Provides alert/notification management functionality with carousel support and persistence.
 *
 * This class manages site-wide alerts including Swiper carousel initialization for multiple alerts,
 * close functionality with localStorage persistence, and proper DOM manipulation.
 *
 * @remarks
 * - Automatically initializes Swiper carousel for multiple alerts
 * - Handles close button functionality with localStorage persistence
 * - Manages alert visibility based on user preferences
 * - Adds appropriate body classes for styling hooks
 *
 * @example
 * // Expected HTML structure (generated by alerts-alert.ascx.vb):
 * // <div class="alert-container">
 * //   <div class="swiper-alert">
 * //     <div class="swiper-wrapper">
 * //       <div class="swiper-slide">
 * //         <div class="alert-container__alert">Alert content</div>
 * //       </div>
 * //     </div>
 * //   </div>
 * //   <button class="swiper-button-prev"></button>
 * //   <button class="swiper-button-next"></button>
 * //   <span class="alert-container__close">×</span>
 * // </div>
 *
 * // Initialization:
 * const alertsPlugin = new Alerts();
 *
 * @public
 */

// Import Swiper and modules
import { Swiper } from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

export class Alerts {
  private alertsCount: number = 0;
  private rootURL: string;

  /**
   * Creates a new Alerts instance and initializes alert functionality.
   */
  constructor() {
    this.rootURL = window.location.protocol + "//" + window.location.host;
    this.init();
  }

  /**
   * Initialize alert functionality
   * @private
   */
  private init(): void {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupAlerts());
    } else {
      this.setupAlerts();
    }
  }

  /**
   * Set up all alert functionality
   * @private
   */
  private setupAlerts(): void {
    this.countAlerts();
    this.initSwiper();
    this.setupCloseButtons();
    this.checkPersistence();
    this.addBodyClass();
  }

  /**
   * Count the number of alerts on the page
   * @private
   */
  private countAlerts(): void {
    this.alertsCount = document.querySelectorAll('.alert-container .swiper-slide').length;
  }

  /**
   * Initialize Swiper carousel for alerts
   * @private
   */
  private initSwiper(): void {
    const alertsContainer = document.querySelector('.alert-container');
    if (!alertsContainer) return;

    const alerts = alertsContainer.querySelectorAll('.swiper-slide');

    // Only initialize Swiper if there are multiple alerts
    if (alerts.length > 1) {
      const swiperElement = alertsContainer.querySelector('.swiper-alert');
      if (swiperElement) {
        try {
          new Swiper('.swiper-alert', {
            modules: [Navigation, Pagination],
            slidesPerView: 1,
            centeredSlides: true,
            loop: true,
            pagination: {
              enabled: true,
              clickable: true,
              el: '.swiper-pagination',
              bulletClass: 'swiper-pagination-bullet alerts-pagination-bullet',
              bulletActiveClass: 'swiper-pagination-bullet-active'
            },
            navigation: {
              enabled: true,
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev'
            }
          });

          swiperElement.classList.add('multiple');
        } catch (e: unknown) {
          console.warn('Swiper initialization failed:', e);
        }
      }
    }
  }

  /**
   * Set up close button event listeners
   * @private
   */
  private setupCloseButtons(): void {
    const closeButtons = document.querySelectorAll('.alert-container__close');
    closeButtons.forEach((button) => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        this.closeAlert(button as HTMLElement);
      });
    });
  }

  /**
   * Close alert and persist user preference
   * @private
   */
  private closeAlert(button: HTMLElement): void {
    const alertContainer = button.closest('.alert-container');
    if (alertContainer) {
      alertContainer.remove();

      // Store in localStorage that user has dismissed alerts
      localStorage.setItem('btnClicked', 'true');

      // Remove body class if no more visible alerts
      const visibleAlerts = document.querySelectorAll('.alert-container');
      if (visibleAlerts.length === 0) {
        document.body.classList.remove('alerts-enabled');
      }
    }
  }

  /**
   * Check localStorage for alert persistence and apply visibility
   * @private
   */
  private checkPersistence(): void {
    const clicked: string | null = localStorage.getItem("btnClicked");
    const alerts = document.querySelectorAll('.alert-container');

    if (clicked) {
      alerts.forEach(alert => alert.remove());
    } else {
      alerts.forEach(alert => {
        const alertElement = alert as HTMLElement;
        alertElement.style.display = 'block';
      });
    }
  }

  /**
   * Add body class if alerts are present
   * @private
   */
  private addBodyClass(): void {
    if (document.querySelectorAll('.alert-container').length > 0) {
      document.body.classList.add('alerts-enabled');
    }
  }

  /**
   * Get the current alert count
   * @returns The number of alerts on the page
   * @public
   */
  public getAlertsCount(): number {
    return this.alertsCount;
  }

  /**
   * Refresh alert functionality (useful after dynamic content changes)
   * @public
   */
  public refresh(): void {
    this.setupAlerts();
  }
}